import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Search, Heart, ChevronDown, User } from "lucide-react";
import CartDrawer from "@/components/CartDrawer";
import SiteSearchDialog from "@/components/SiteSearchDialog";
import LanguageSelector from "@/components/LanguageSelector";


const brandLinks = [
  { label: "Yaphene", href: "/brands/yaphene" },
  { label: "Marapa Skincare", href: "/brands/marapa" },
  { label: "Nezer Men Skincare", href: "/brands/nezer" },
  { label: "Calumé", href: "/brands/calume" },
  { label: "Flauriel Foods", href: "/brands/flauriel" },
  { label: "Herbz Kulture", href: "/brands/herbz-kulture" },
  { label: "Tallivo", href: "/brands/tallivo" },
  { label: "AnaElli Digital", href: "/brands/anaelli" },
];

const storyLinks = [
  { label: "Our Story", href: "/our-story" },
  { label: "Company Profile", href: "/company-profile" },
  { label: "Company Timeline", href: "/company-timeline" },
  { label: "Our Philosophy", href: "/philosophy" },
  { label: "The Island Is Our First Teacher", href: "/island-first-teacher" },
  { label: "Caribbean Legacy House", href: "/caribbean-legacy-house" },
  { label: "Legacy & Impact", href: "/legacy-impact" },
  { label: "Meet the Founder", href: "/meet-the-founder" },
  { label: "Mentorship & Advisory", href: "/mentorship-advisory" },
  { label: "Integrative Wellness Intensive", href: "/integrative-wellness-intensive" },
  { label: "Press & Features", href: "/press-and-recognition" },
  { label: "Awards & Recognition", href: "/caribbean-posh-wellness-innovation-icon-award-2026" },
  { label: "Community", href: "/community" },
  { label: "Visit Di Organics Shoppe", href: "/visit-di-organics-shoppe" },
  { label: "Virtual Shop", href: "/virtual-shop" },
  { label: "Island Library", href: "/island-library" },
  { label: "Our Social Work", href: "/social-work" },
  { label: "Hospitality, Gifting & Partnerships", href: "/partnerships" },
];

const experienceLinks = [
  { label: "Balm and Bond", href: "/experiences/balm-and-bond" },
  { label: "Creative Immersion Summer Camp", href: "/experiences/creative-immersion" },
  { label: "Sunrise and Stillness", href: "/experiences/sunrise-and-stillness" },
  { label: "Events & Pop-Ups", href: "/events" },
  { label: "Merch & Lifestyle", href: "/merch" },
];

// Grouped under "More" so the desktop bar fits beside the cart at laptop widths.
const moreLinks = [
  { label: "Rooted Ritual", href: "/rooted-ritual" },
  { label: "Journal", href: "/blog" },
  { label: "Wholesale", href: "/wholesale" },
  { label: "FAQ", href: "/faq" },
];

const discoverLinks = [
  { label: "Wellness Knowledge Library", href: "/learn" },
  { label: "About Our Island", href: "/about-our-island" },
  { label: "Discover St. Kitts", href: "/discover-st-kitts" },
  { label: "Discover Nevis", href: "/discover-nevis" },
  { label: "Taste of St. Kitts", href: "/discover-st-kitts/taste-of-st-kitts" },
  { label: "Living Memory of St. Kitts", href: "/kittitian-culture" },
  { label: "History & Heritage", href: "/history-and-heritage" },
  { label: "The Living Archive", href: "/archive" },
  { label: "The Story of Money", href: "/discover-st-kitts/history-of-money" },
  { label: "Nature & Geography", href: "/discover-st-kitts/nature-geography" },
  { label: "Festivals & Traditions", href: "/discover-st-kitts/festivals-and-traditions" },
  { label: "Experience It Like a Local", href: "/discover-st-kitts/experience-it-like-a-local" },
  { label: "Before You Arrive", href: "/discover-st-kitts/curious-visitor-faq" },
];

interface DropdownProps {
  label: string;
  links: { label: string; href: string }[];
  align?: "left" | "right";
  className?: string;
}

const NavDropdown = ({ label, links, align = "left", className = "" }: DropdownProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="true"
        className="flex items-center gap-1 whitespace-nowrap text-sm font-sans font-medium text-foreground/80 hover:text-primary transition-colors"
      >
        {label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className={`absolute top-full ${align === "right" ? "right-0" : "left-0"} mt-2 bg-background border border-border rounded-md shadow-lg py-2 z-50 animate-fade-in-up ${links.length > 6 ? "w-[36rem] grid grid-cols-2 gap-x-2" : "w-56"}`}>
          {links.map((link) => (
            <Link
              key={link.label}
              to={link.href}
              className="block px-4 py-2 text-sm font-sans text-foreground/80 hover:text-primary hover:bg-secondary/50 transition-colors"
              onClick={() => setOpen(false)}
            >
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileBrandsOpen, setMobileBrandsOpen] = useState(false);
  const [mobileStoryOpen, setMobileStoryOpen] = useState(false);
  const [mobileExpOpen, setMobileExpOpen] = useState(false);
  const [mobileGuidesOpen, setMobileGuidesOpen] = useState(false);
  const [mobileDiscoverOpen, setMobileDiscoverOpen] = useState(false);

  // Lock background scroll when mobile menu is open; preserve scroll position.
  useEffect(() => {
    if (!mobileOpen) return;
    const scrollY = window.scrollY;
    const body = document.body;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    body.style.overflow = "hidden";
    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [mobileOpen]);

  return (
    <header className="bg-background/95 backdrop-blur-sm sticky top-0 z-50 border-b border-border">
      <div className="container mx-auto flex items-center justify-between gap-2 min-[360px]:gap-4 py-4 px-3 min-[360px]:px-4">
        <Link to="/" className="flex shrink-0 flex-col items-center">
          <span className="font-serif text-lg min-[360px]:text-xl font-bold text-primary tracking-wide">
            Sugar Town
          </span>
          <span className="text-xs tracking-[0.3em] text-muted-foreground uppercase">
            Organics
          </span>
        </Link>

        <nav className="hidden xl:flex min-w-0 items-center gap-3 2xl:gap-5 whitespace-nowrap" aria-label="Main navigation">
          <Link to="/shop" className="text-sm font-sans font-medium text-foreground/80 hover:text-primary transition-colors">
            Shop
          </Link>
          <NavDropdown label="Guides" links={[
            { label: "Hair Care Guide", href: "/natural-hair-care-black-women" },
            { label: "Skincare Guide", href: "/caribbean-botanical-skincare" },
            { label: "Shop by Concern", href: "/shop-by-concern" },
          ]} />
          <NavDropdown label="Our Worlds" links={brandLinks} />
          <NavDropdown label="The Legacy" links={storyLinks} />
          <NavDropdown label="Gather" links={experienceLinks} />
          <Link
            to="/caribbean-heritage-longevity-method"
            className="text-sm font-sans font-semibold text-accent hover:text-primary transition-colors uppercase tracking-wider"
          >
            Caribbean Longevity™
          </Link>
          <NavDropdown label="Discover St. Kitts" links={discoverLinks} />
          <NavDropdown label="More" links={moreLinks} align="right" />
        </nav>

        <div className="flex shrink-0 items-center gap-0 min-[360px]:gap-1 sm:gap-2 2xl:gap-3">
          <button
            onClick={() => setSearchOpen(true)}
            className="p-2 text-foreground/70 hover:text-primary transition-colors"
            aria-label="Search"
          >
            <Search className="w-5 h-5" />
          </button>
          <LanguageSelector />
          <button className="p-2 text-foreground/70 hover:text-primary transition-colors hidden sm:block" aria-label="Wishlist">
            <Heart className="w-5 h-5" />
          </button>
          <Link to="/account-native" className="flex items-center gap-1 p-2 text-foreground/70 hover:text-primary transition-colors" aria-label="Sign in or view my account">
            <User aria-hidden="true" className="w-5 h-5" />
            <span className="hidden md:inline xl:hidden text-xs font-sans font-medium">Sign in</span>
          </Link>
          <CartDrawer />
          <button
            className="xl:hidden p-1.5 min-[360px]:p-2 text-foreground/70"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          className="xl:hidden fixed inset-0 z-[60] flex flex-col bg-background"
          style={{
            height: "100dvh",
            maxHeight: "100dvh",
            paddingTop: "env(safe-area-inset-top)",
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation"
        >
          {/* Fixed header row inside drawer */}
          <div className="shrink-0 flex items-center justify-between px-4 py-3 border-b border-border bg-background">
            <Link
              to="/"
              className="flex flex-col items-center"
              onClick={() => setMobileOpen(false)}
            >
              <span className="font-serif text-lg font-bold text-primary tracking-wide">
                Sugar Town
              </span>
              <span className="text-[10px] tracking-[0.3em] text-muted-foreground uppercase">
                Organics
              </span>
            </Link>
            <button
              className="p-2 text-foreground/70"
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Scrollable menu body */}
          <nav
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain flex flex-col px-6 py-4 gap-1 [-webkit-overflow-scrolling:touch]"
            style={{
              paddingBottom: "calc(2rem + env(safe-area-inset-bottom))",
            }}
          >
            <Link to="/shop" className="text-sm font-sans font-medium text-foreground/80 hover:text-primary py-2 transition-colors" onClick={() => setMobileOpen(false)}>Shop</Link>
            <Link to="/account-native" className="text-sm font-sans font-medium text-foreground/80 hover:text-primary py-2 transition-colors" onClick={() => setMobileOpen(false)}>Sign in / My account</Link>
            {/* Guides accordion */}
            <button
              onClick={() => setMobileGuidesOpen(!mobileGuidesOpen)}
              className="flex items-center justify-between text-sm font-sans font-medium text-foreground/80 hover:text-primary py-2 transition-colors"
            >
              Guides
              <ChevronDown className={`w-4 h-4 transition-transform ${mobileGuidesOpen ? "rotate-180" : ""}`} />
            </button>
            {mobileGuidesOpen && (
              <div className="pl-4 flex flex-col gap-1">
                <Link to="/natural-hair-care-black-women" className="text-sm font-sans text-foreground/70 hover:text-primary py-1.5 transition-colors" onClick={() => setMobileOpen(false)}>Hair Care Guide</Link>
                <Link to="/caribbean-botanical-skincare" className="text-sm font-sans text-foreground/70 hover:text-primary py-1.5 transition-colors" onClick={() => setMobileOpen(false)}>Skincare Guide</Link>
                <Link to="/shop-by-concern" className="text-sm font-sans text-foreground/70 hover:text-primary py-1.5 transition-colors" onClick={() => setMobileOpen(false)}>Shop by Concern</Link>
              </div>
            )}
            {/* Our Worlds accordion */}
            <button
              onClick={() => setMobileBrandsOpen(!mobileBrandsOpen)}
              className="flex items-center justify-between text-sm font-sans font-medium text-foreground/80 hover:text-primary py-2 transition-colors"
            >
              Our Worlds
              <ChevronDown className={`w-4 h-4 transition-transform ${mobileBrandsOpen ? "rotate-180" : ""}`} />
            </button>
            {mobileBrandsOpen && (
              <div className="pl-4 flex flex-col gap-1">
                {brandLinks.map((link) => (
                  <Link
                    key={link.label}
                    to={link.href}
                    className="text-sm font-sans text-foreground/70 hover:text-primary py-1.5 transition-colors"
                    onClick={() => setMobileOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}

            {/* The Legacy accordion */}
            <button
              onClick={() => setMobileStoryOpen(!mobileStoryOpen)}
              className="flex items-center justify-between text-sm font-sans font-medium text-foreground/80 hover:text-primary py-2 transition-colors"
            >
              The Legacy
              <ChevronDown className={`w-4 h-4 transition-transform ${mobileStoryOpen ? "rotate-180" : ""}`} />
            </button>
            {mobileStoryOpen && (
              <div className="pl-4 flex flex-col gap-1">
                {storyLinks.map((link) => (
                  <Link
                    key={link.label}
                    to={link.href}
                    className="text-sm font-sans text-foreground/70 hover:text-primary py-1.5 transition-colors"
                    onClick={() => setMobileOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}

            {/* Gather accordion */}
            <button
              onClick={() => setMobileExpOpen(!mobileExpOpen)}
              className="flex items-center justify-between text-sm font-sans font-medium text-foreground/80 hover:text-primary py-2 transition-colors"
            >
              Gather
              <ChevronDown className={`w-4 h-4 transition-transform ${mobileExpOpen ? "rotate-180" : ""}`} />
            </button>
            {mobileExpOpen && (
              <div className="pl-4 flex flex-col gap-1">
                {experienceLinks.map((link) => (
                  <Link
                    key={link.label}
                    to={link.href}
                    className="text-sm font-sans text-foreground/70 hover:text-primary py-1.5 transition-colors"
                    onClick={() => setMobileOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}

            <Link to="/rooted-ritual" className="text-sm font-sans font-medium text-foreground/80 hover:text-primary py-2 transition-colors" onClick={() => setMobileOpen(false)}>Rooted Ritual</Link>

            <Link to="/caribbean-heritage-longevity-method" className="text-sm font-sans font-semibold text-accent hover:text-primary py-2 transition-colors uppercase tracking-wider" onClick={() => setMobileOpen(false)}>Caribbean Longevity™</Link>

            {/* Discover St. Kitts accordion */}
            <button
              onClick={() => setMobileDiscoverOpen(!mobileDiscoverOpen)}
              className="flex items-center justify-between text-sm font-sans font-medium text-foreground/80 hover:text-primary py-2 transition-colors"
            >
              Discover St. Kitts
              <ChevronDown className={`w-4 h-4 transition-transform ${mobileDiscoverOpen ? "rotate-180" : ""}`} />
            </button>
            {mobileDiscoverOpen && (
              <div className="pl-4 flex flex-col gap-1">
                {discoverLinks.map((link) => (
                  <Link
                    key={link.label}
                    to={link.href}
                    className="text-sm font-sans text-foreground/70 hover:text-primary py-1.5 transition-colors"
                    onClick={() => setMobileOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}

            <Link to="/blog" className="text-sm font-sans font-medium text-foreground/80 hover:text-primary py-2 transition-colors" onClick={() => setMobileOpen(false)}>Journal</Link>

            <Link to="/wholesale" className="text-sm font-sans font-medium text-foreground/80 hover:text-primary py-2 transition-colors" onClick={() => setMobileOpen(false)}>Wholesale</Link>
            <Link to="/faq" className="text-sm font-sans font-medium text-foreground/80 hover:text-primary py-2 transition-colors" onClick={() => setMobileOpen(false)}>FAQ</Link>
          </nav>
        </div>
      )}

      <SiteSearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </header>
  );
};

export default Header;
